import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Mail, Lock, User, Loader2 } from 'lucide-react';
import { ShhIcon } from '@/components/icons/ShhIcon';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const wardrobeBackgroundUrl = `url(${process.env.PUBLIC_URL || ''}/wardrobe-bg.jpg)`;

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ 
    username: '', 
    email: '', 
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (formData.username.length < 2) {
      toast.error('Display name must be at least 2 characters');
      return;
    }

    setLoading(true);
    try {
      const result = await register(formData.username, formData.email, formData.password);
      setSuccess(true);
      toast.success('Please check your email to verify your account.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-background">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="max-w-md w-full text-center">
            <CardContent className="pt-10 pb-8">
              <div className="w-16 h-16 rounded-full bg-forest-100 dark:bg-forest-900/30 mx-auto flex items-center justify-center mb-6">
                <Mail className="w-8 h-8 text-forest-600" />
              </div>
              <h2 className="font-serif text-2xl font-semibold mb-2">Check Your Email</h2>
              <p className="text-muted-foreground mb-4">
                We've sent a verification email to <strong>{formData.email}</strong>.
              </p>
              <p className="text-muted-foreground mb-6">
                Simply <strong>reply to that email</strong> to verify your account. That's it — just hit reply and send!
              </p>
              
              <div className="bg-muted/50 rounded-lg p-4 mb-6 text-sm text-muted-foreground">
                <p>Didn't get the email? Check your spam folder, or try logging in — you can resend from there.</p>
              </div>

              <Button 
                variant="outline" 
                onClick={() => navigate('/login')}
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg overflow-hidden">
              <ShhIcon className="w-full h-full" />
            </div>
            <span className="font-serif font-bold text-xl">Secrets Club</span>
          </div>

          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0">
              <CardTitle className="font-serif text-3xl">Create Account</CardTitle>
              <CardDescription className="text-base">
                Join our community of anonymous confessors
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0 space-y-6">
              {/* Social Login Buttons */}
              <div className="space-y-3">
                <p className="text-center text-sm font-medium text-forest-700 dark:text-forest-300">
                  1 Click, instant verification
                </p>
                <GoogleSignInButton
                  testId="google-signup-btn"
                  text="continue_with"
                  onSuccess={() => {
                    toast.success('Welcome to Secrets Club!');
                    navigate('/', { replace: true });
                  }}
                />
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or register with email
                  </span>
                </div>
              </div>

              {/* Registration Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Display Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="username"
                      name="username"
                      type="text"
                      placeholder="Your public display name"
                      className="pl-10 h-12"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      data-testid="display-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      className="pl-10 h-12"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10 h-12"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10 h-12"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 btn-primary text-base"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-forest-700 dark:text-forest-400 font-medium hover:underline" data-testid="login-link">
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Right Panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: wardrobeBackgroundUrl,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-forest-950/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="w-16 h-16 rounded-xl overflow-hidden mb-6">
              <ShhIcon className="w-full h-full" />
            </div>
            <h1 className="font-serif text-4xl font-bold mb-4">Join the Club</h1>
            <p className="text-lg text-white/80 max-w-md leading-relaxed">
              Share your deepest thoughts anonymously. Find advice, comfort, and connection in a judgment-free space.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
